import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
    FlyFreelyLoggingService,
    OrganisationRelationshipDto,
    OrganisationRelationshipService,
    PersonsOrganisationDto,
    ShareSiteCommand,
    SimpleSiteDto,
    Site,
    UnshareSiteCommand,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { Angulartics2 } from 'angulartics2';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OrganisationRelationshipsUiService } from './organisation-relationships-ui.service';

@Component({
    selector: 'shared-sites',
    styleUrls: ['./common.scss'],
    template: `<h5 [ngClass]="{ readonly: working }">Shared Sites</h5>
        <div class="input-group">
            <ng-select
                [items]="
                    organisationSiteList$
                        | async
                        | sortBy: 'name'
                        | filterOutById: relationship.sharedSiteList
                "
                bindValue="id"
                bindLabel="name"
                appendTo="body"
                [formControl]="addSite"
                placeholder="Select site to share with {{
                    relationship.childOrganisation.name
                }}"
                [readonly]="working || isReadOnlyAdmin"
                [clearable]="false"
            >
            </ng-select>
            <span class="input-group-btn">
                <button
                    type="button"
                    class="btn btn-primary"
                    [disabled]="working || !addSite.valid || isReadOnlyAdmin"
                    (click)="shareSite()"
                >
                    Share
                </button>
            </span>
        </div>
        <div>
            <div
                *ngFor="
                    let site of relationship.sharedSiteList | sortBy: 'name'
                "
                class="top-buffer horizontal-container center-children"
            >
                <span class="shared-item">{{ site.name }}</span>

                <button
                    *ngIf="!isReadOnlyAdmin"
                    type="button"
                    class="btn btn-tertiary btn-delete"
                    tooltip="Remove sharing of site"
                    placement="top"
                    (click)="unshareSite(site)"
                >
                    <span class="fal fa-trash-alt"></span>
                </button>
            </div>
        </div>`
})
export class SharedSites implements OnInit, OnDestroy {
    @Input() organisation: PersonsOrganisationDto;
    @Input() relationship: OrganisationRelationshipDto;
    @Input() isReadOnlyAdmin = false;

    working = false;
    private ngUnsubscribe$ = new Subject<void>();

    organisationSiteList$: Observable<Site[]>;

    addSite = new FormControl(undefined, [Validators.required]);

    constructor(
        public workTracker: WorkTracker,
        private logging: FlyFreelyLoggingService,
        private angulartics2: Angulartics2,
        private organisationRelationshipService: OrganisationRelationshipService,
        private organisationRelationshipsUi: OrganisationRelationshipsUiService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.organisationSiteList$ =
            this.organisationRelationshipsUi.organisationSiteList$;
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    shareSite() {
        const cmd: ShareSiteCommand = {
            siteId: this.addSite.value
        };
        this.organisationRelationshipService
            .shareSite(this.organisation.id, this.relationship.id, cmd)
            .subscribe({
                next: results => {
                    this.addSite.setValue(undefined);
                    this.angulartics2.eventTrack.next({
                        action: 'share-site',
                        properties: {
                            category: 'shared-resources',
                            label: results.id
                        }
                    });
                },
                error: error =>
                    this.logging.error(error, 'Error while sharing site')
            })
            .add(this.workTracker.createTracker());
    }

    unshareSite(site: SimpleSiteDto) {
        const cmd: UnshareSiteCommand = { siteId: site.id };
        this.organisationRelationshipService
            .unshareSite(this.organisation.id, this.relationship.id, cmd)
            .subscribe({
                next: result => {
                    this.angulartics2.eventTrack.next({
                        action: 'unshare-site',
                        properties: {
                            category: 'shared-resources',
                            label: result.id
                        }
                    });
                },
                error: error =>
                    this.logging.error(error, 'Error while unsharing site')
            })
            .add(this.workTracker.createTracker());
    }
}
